import { Trans, useLingui } from "@lingui/react/macro";
import { CircleAlertIcon, XIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { buildExpandedImagePreview, type ExpandedImagePreview } from "./ExpandedImagePreview";
import { cn } from "~/lib/utils";
import type { ChatAttachment, ChatImageAttachment } from "../../types";

const MAX_VISIBLE_TILES = 10;

const TILE_CLASS_NAME =
  "relative min-h-17 shrink-0 basis-[calc((100%-0.75rem)/3)] overflow-hidden rounded-lg border @lg:basis-[calc((100%-1.5rem)/5)]";

function formatAttachmentSize(sizeBytes: number): string {
  if (!Number.isFinite(sizeBytes) || sizeBytes < 0) return "";
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = sizeBytes / 1024;
  for (const unit of units) {
    if (value < 1024 || unit === units.at(-1)) {
      return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${unit}`;
    }
    value /= 1024;
  }
  return `${sizeBytes} B`;
}

function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toUpperCase();
}

type AttachmentsBlockVariant = "composer" | "message";

interface AttachmentsBlockProps {
  attachments: ReadonlyArray<ChatAttachment>;
  variant: AttachmentsBlockVariant;
  /** Expand an image into the lightbox. When omitted, images are not clickable. */
  onExpandImage?: (preview: ExpandedImagePreview) => void;
  /** Remove an attachment from the draft. When omitted, no remove control is shown. */
  onRemove?: (attachment: ChatAttachment) => void;
  /** Ids of image attachments whose draft could not be persisted (composer only). */
  unpersistedImageIds?: ReadonlySet<string>;
  className?: string;
}

/**
 * Grid of attachment tiles shared by the composer (with remove controls) and
 * the message timeline (right-aligned above the user bubble). Images fit
 * inside the tile, files show the full name with extension and size. Past
 * MAX_VISIBLE_TILES the last cell counts the hidden attachments.
 */
export function AttachmentsBlock({
  attachments,
  variant,
  onExpandImage,
  onRemove,
  unpersistedImageIds,
  className,
}: AttachmentsBlockProps) {
  const { t } = useLingui();
  if (attachments.length === 0) return null;

  const images = attachments.filter(
    (attachment): attachment is ChatImageAttachment => attachment.type === "image",
  );
  const visible =
    attachments.length > MAX_VISIBLE_TILES
      ? attachments.slice(0, MAX_VISIBLE_TILES - 1)
      : attachments;
  const hiddenCount = attachments.length - visible.length;
  const borderClassName = variant === "composer" ? "border-border/80" : "border-border";

  const expandImage = (image: ChatImageAttachment) => {
    if (!onExpandImage) return;
    const preview = buildExpandedImagePreview(images, image.id);
    if (!preview) return;
    onExpandImage(preview);
  };

  return (
    <div className={cn("@container min-w-0", variant === "composer" && "mb-3", className)}>
      <div className={cn("flex flex-wrap gap-1.5", variant === "message" && "justify-end")}>
        {visible.map((attachment) => (
          <AttachmentTile
            key={attachment.id}
            attachment={attachment}
            borderClassName={borderClassName}
            onExpand={
              attachment.type === "image" && onExpandImage
                ? () => expandImage(attachment)
                : undefined
            }
            onRemove={onRemove ? () => onRemove(attachment) : undefined}
            unpersisted={unpersistedImageIds?.has(attachment.id) ?? false}
          />
        ))}
        {hiddenCount > 0 && (
          <div
            className={cn(
              TILE_CLASS_NAME,
              "flex items-center justify-center border-dashed border-border text-xs text-muted-foreground",
            )}
          >
            {t`+${hiddenCount} more`}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentTile({
  attachment,
  borderClassName,
  onExpand,
  onRemove,
  unpersisted,
}: {
  attachment: ChatAttachment;
  borderClassName: string;
  onExpand?: (() => void) | undefined;
  onRemove?: (() => void) | undefined;
  unpersisted: boolean;
}) {
  const { t } = useLingui();
  const previewUrl = attachment.type === "image" ? attachment.previewUrl : undefined;
  return (
    <div
      className={cn(TILE_CLASS_NAME, borderClassName, "bg-secondary/25")}
      title={attachment.name}
    >
      {previewUrl && onExpand ? (
        <button
          type="button"
          className="absolute inset-0 cursor-zoom-in"
          aria-label={t`Preview ${attachment.name}`}
          onClick={onExpand}
        >
          <img src={previewUrl} alt={attachment.name} className="size-full object-contain" />
        </button>
      ) : previewUrl ? (
        <img
          src={previewUrl}
          alt={attachment.name}
          className="absolute inset-0 size-full object-contain"
        />
      ) : (
        <FileTileBody name={attachment.name} sizeBytes={attachment.sizeBytes} />
      )}
      {unpersisted && (
        <Tooltip>
          <TooltipTrigger
            render={
              <span
                role="img"
                aria-label={t`Draft attachment may not persist`}
                className="absolute left-1 top-1 inline-flex items-center justify-center rounded bg-background/85 p-0.5 text-amber-600"
              >
                <CircleAlertIcon className="size-3" />
              </span>
            }
          />
          <TooltipPopup side="top" className="max-w-64 whitespace-normal leading-tight">
            <Trans>
              Draft attachment could not be saved locally and may be lost on navigation.
            </Trans>
          </TooltipPopup>
        </Tooltip>
      )}
      {onRemove && (
        <Button
          variant="ghost"
          size="icon-xs"
          className="absolute bottom-1 right-1 bg-background/80 hover:bg-background/90"
          onClick={onRemove}
          aria-label={t`Remove ${attachment.name}`}
        >
          <XIcon />
        </Button>
      )}
    </div>
  );
}

function FileTileBody({ name, sizeBytes }: { name: string; sizeBytes: number }) {
  const extension = fileExtension(name);
  return (
    <div className="flex size-full flex-col justify-between gap-1 px-2.5 py-2">
      <div className="text-xs leading-[1.3] wrap-anywhere text-foreground">{name}</div>
      <div className="flex gap-1.5 truncate text-[11px] leading-4 text-muted-foreground">
        {extension && <span>{extension}</span>}
        <span>{formatAttachmentSize(sizeBytes)}</span>
      </div>
    </div>
  );
}
